/** API-UL EDITORULUI, citit din sursa lui.
 *
 *  DE CE NU E O LISTĂ SCRISĂ DE MÂNĂ. Editorul are peste o sută de pârghii și
 *  crește la fiecare gest nou de pe pânză. O listă scrisă de mână în promptul
 *  asistentului ar rămâne în urmă tăcut — exact motivul pentru care structura
 *  documentului se citește din schema catalogului și nu se scrie (v.
 *  `asistent/schema.py`). Aici sursa e chiar `editor/model.ts`: numele,
 *  semnătura și prima propoziție a comentariului fiecărei funcții exportate.
 *  O funcție nouă în editor e o funcție pe care asistentul o are din clipa în
 *  care e scrisă, fără să se atingă nimic aici.
 *
 *  CE ANUME SE EXTRAGE: numele, parametrii, tipul întors când încape pe un rând,
 *  și primul paragraf al comentariului. NU corpul: ce face funcția se citește
 *  din explicația ei, iar patru mii de rânduri de implementare n-ar încăpea în
 *  nicio discuție.
 *
 *  SURSA VINE ÎNTR-UN CHUNK PROPRIU, cerut abia când modelul chiar întreabă de
 *  API (`import()`, nu `import`): 230 KB de text care nu se descarcă în
 *  editorul cuiva care n-a deschis asistentul.
 */

/** O pârghie a editorului, așa cum o citește modelul. */
export type Functie = {
  nume: string;
  /** `mutaPeretele(doc, i, dep, opt?): TronsonDoc` */
  semnatura: string;
  /** Primul paragraf al comentariului, gol dacă funcția n-are unul. */
  doc: string;
  /** NUMELE ȘI COMENTARIUL ÎNTREG, fără diacritice și cu litere mici — numai
   *  de căutat, nu se trimite nicăieri.
   *
   *  ÎNTREG, nu primul paragraf: explicația de ce există o funcție stă mai jos
   *  în comentariu, iar cuvântul după care o caută cineva («hol», «lățime»,
   *  «lipire») e tocmai acolo. Căutarea pe primul paragraf tăiat la 240 de
   *  caractere găsea funcția numai dacă îi ghiceai deja numele. */
  cauta: string;
};

/** Fără diacritice, cu litere mici. Cine caută scrie «latime», sursa scrie
 *  «lățime», și amândoi au dreptate. */
export function normalizeaza(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

const MAX_DOC = 240;
const MAX_VALOARE = 90;
const MAX_PARAMETRI = 300;

/** Fișierele din care se citește API-ul, cu numele sub care le vede modelul.
 *
 *  Ordinea e cea în care se suprascriu numele în `motor::API`: ce e în
 *  `editor/model` bate tot, fiindcă acolo stau gesturile. */
export const SURSE: Record<string, () => Promise<{ default: string }>> = {
  doc: () => import("../viewer/tronson/doc.ts?raw"),
  calc: () => import("../viewer/tronson/calc.ts?raw"),
  feluri: () => import("../viewer/tronson/feluri.ts?raw"),
  "design/finisaje": () => import("../viewer/tronson/design/finisaje.tsx?raw"),
  "design/straturi": () => import("../viewer/tronson/design/straturi.ts?raw"),
  "design/auto": () => import("../viewer/tronson/design/auto.ts?raw"),
  "editor/model": () => import("../viewer/tronson/editor/model.ts?raw"),
};

/** Perechea lui `(` de la `i`, sau −1. Numai parantezele rotunde: într-o
 *  semnătură, ce e între acolade e un tip, nu o paranteză de închis. */
function parenPereche(sursa: string, i: number): number {
  let adancime = 0;
  for (let k = i; k < sursa.length; k++) {
    if (sursa[k] === "(") adancime++;
    else if (sursa[k] === ")" && --adancime === 0) return k;
  }
  return -1;
}

/** Parametrii, pe un rând, fără comentarii.
 *
 *  Comentariile DIN parametri sunt lungi: un obiect de opțiuni din `model.ts`
 *  își explică fiecare cheie pe zece rânduri, iar lipite într-o semnătură
 *  ieșeau 1.300 de caractere — măsurat pe `mutaPeretele`, care singură ar fi
 *  fost mai mare decât toată lista. Cheile rămân (numele lor e ce caută
 *  modelul), explicațiile lor nu. */
function curata(parametri: string): string {
  const pe_un_rand = parametri
    .replace(/\/\*[\s\S]*?\*\//g, " ")
    .replace(/\/\/[^\n]*/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\s*([;,])\s*/g, "$1 ")
    .trim()
    .replace(/[;,]$/, "");
  return pe_un_rand.length > MAX_PARAMETRI
    ? `${pe_un_rand.slice(0, MAX_PARAMETRI).trimEnd()}…` : pe_un_rand;
}

/** Comentariul care se termină exact înaintea lui `pana`, primul paragraf.
 *
 *  Un paragraf, nu tot: comentariile din editor au și antetul lung cu de ce
 *  s-a ales soluția aia, care e scris pentru cine schimbă funcția, nu pentru
 *  cine o cheamă. */
function docDinainte(sursa: string, pana: number): string {
  const inainte = sursa.slice(0, pana).trimEnd();
  if (!inainte.endsWith("*/")) return "";
  const start = inainte.lastIndexOf("/**");
  if (start < 0) return "";
  const randuri: string[] = [];
  for (const rand of inainte.slice(start + 3, inainte.length - 2).split("\n")) {
    const text = rand.replace(/^\s*\*/, "").trim();
    if (!text) {
      if (randuri.length) break;                // paragraful s-a terminat
      continue;                                 // rânduri goale de la început
    }
    randuri.push(text);
  }
  const doc = randuri.join(" ").replace(/\s+/g, " ").trim();
  return doc.length > MAX_DOC ? `${doc.slice(0, MAX_DOC).trimEnd()}…` : doc;
}

/** Comentariul ÎNTREG de dinaintea lui `pana`, toate paragrafele, ca text de
 *  căutat. */
function comentariulIntreg(sursa: string, pana: number): string {
  const inainte = sursa.slice(0, pana).trimEnd();
  if (!inainte.endsWith("*/")) return "";
  const start = inainte.lastIndexOf("/**");
  if (start < 0) return "";
  return inainte.slice(start + 3, inainte.length - 2)
    .replace(/^\s*\*/gm, " ").replace(/\s+/g, " ").trim();
}

/** Pârghiile exportate de o sursă, în ordinea din fișier.
 *
 *  Ordinea din fișier nu e o comoditate: în `model.ts` funcțiile stau grupate
 *  pe familii — pereți, camere, goluri, mobilier, electrice —, iar lista
 *  citită în ordine se citește ca un cuprins. Alfabetic, s-ar fi amestecat. */
export function extrage(sursa: string): Functie[] {
  const gasite: { la: number; f: Functie }[] = [];

  const functii = /^export (?:async )?function (\w+)\s*(?:<[^(]*>)?\(/gm;
  for (let m = functii.exec(sursa); m; m = functii.exec(sursa)) {
    // MAJUSCULA LA ÎNCEPUT ÎNSEAMNĂ ALTCEVA, prin convenția paginii: o
    // componentă React (`FinisajeDefs`) sau o clasă (`Istoric`). Niciuna nu e o
    // pârghie a editorului — una întoarce `<defs>`, alta e istoricul paginii,
    // pe care codul asistentului n-are ce construi. Ies din listă ȘI din `E`
    // (v. `motor::API`), ca lista și obiectul să nu poată spune altceva.
    if (m[1][0] === m[1][0].toUpperCase()) continue;
    const deschis = sursa.indexOf("(", m.index + m[0].length - 1);
    const inchis = parenPereche(sursa, deschis);
    if (inchis < 0) continue;
    const parametri = curata(sursa.slice(deschis + 1, inchis));
    // Tipul întors doar când încape pe un rând și n-are acolade: un tip-obiect
    // scris pe zece rânduri nu se poate tăia fără să iasă o minciună, iar ce
    // întoarce funcția se află oricum din explicația ei.
    const coada = /^\)\s*:\s*([^{\s][^\n]*?)\s*\{/
      .exec(sursa.slice(inchis, inchis + 400));
    gasite.push({ la: m.index, f: {
      nume: m[1],
      semnatura: `${m[1]}(${parametri})${coada ? `: ${coada[1]}` : ""}`,
      doc: docDinainte(sursa, m.index),
      cauta: normalizeaza(`${m[1]} ${comentariulIntreg(sursa, m.index)}`),
    } });
  }

  // Constantele exportate sunt și ele API: `LIPIRE`, `FORME_CAMERA`,
  // `MODELE_USA` — praguri și enumerări pe care un cod scris de model trebuie
  // să le folosească, nu să le ghicească.
  const constante = /^export const (\w+)(?::[^=\n]+)?\s*=\s*(.+)$/gm;
  for (let m = constante.exec(sursa); m; m = constante.exec(sursa)) {
    const valoare = m[2].trim();
    gasite.push({ la: m.index, f: {
      nume: m[1],
      semnatura: `${m[1]} = ${valoare.length > MAX_VALOARE
        ? `${valoare.slice(0, MAX_VALOARE).trimEnd()}…` : valoare}`,
      doc: docDinainte(sursa, m.index),
      cauta: normalizeaza(`${m[1]} ${comentariulIntreg(sursa, m.index)}`),
    } });
  }

  return gasite.sort((a, b) => a.la - b.la).map((g) => g.f);
}

/** Editor gestures held back from the assistant until their own spec. The
 *  mounting box, its row indices and the LED strip have no tool or doctrine
 *  yet, and a listed gesture is one the model will reach for; `motor::API`
 *  drops the same names so the list and the object cannot disagree (the sync
 *  test holds them together). `aseazaPeLiniaGrilei` is a step inside the wall
 *  gestures (a wall turned onto 45° lands on a grid line), not one of its own. */
export const HELD_BACK = ["adaugaInDoza", "scoateDinDoza", "lipesteDoza",
                          "schimbaIndiceInDoza", "adaugaBandaLed", "mutaCapatBanda",
                          "aseazaPeLiniaGrilei"];

/** Toate pârghiile, pe fișier. Se citește o dată pe pagină. */
let memorat: Record<string, Functie[]> | null = null;

export async function parghii(): Promise<Record<string, Functie[]>> {
  if (!memorat) {
    const intrari = await Promise.all(
      Object.entries(SURSE).map(async ([nume, ia]) =>
        [nume, extrage((await ia()).default)
          .filter((f) => !HELD_BACK.includes(f.nume))] as const));
    memorat = Object.fromEntries(intrari);
  }
  return memorat;
}

/** TOATE semnăturile, ca text, pentru preîncărcarea din primul mesaj.
 *
 *  DE CE PLEACĂ ÎNTREAGĂ, ODATĂ. Lista costă vreo trei mii și jumătate de
 *  tokeni, scriși o dată pe discuție și citiți pe urmă din cache la a zecea
 *  parte — mai puțin decât UN tur în plus pe `api_editorului`, iar tururile
 *  alea veneau câte două-trei pe cerere, fiindcă numele pârghiilor nu se
 *  deduc. Cu lista în față, căutarea rămâne pentru explicația funcției deja
 *  găsite, nu pentru aflarea ei.
 *
 *  Numai semnăturile, ca și la `suprafata()` fără `caut`: explicațiile tuturor
 *  sunt de patru ori mai mari și ar fi rămas și ele în discuție la fiecare tur.
 */
export async function semnaturile(): Promise<string> {
  const toate = await parghii();
  return Object.entries(toate)
    .map(([fisier, lista]) =>
      `${fisier}:\n${lista.map((f) => `  ${f.semnatura}`).join("\n")}`)
    .join("\n");
}

/** API-ul, în forma care pleacă spre model.
 *
 *  FĂRĂ `caut`, numai semnăturile: lista întreagă cu explicații e de patru ori
 *  mai mare și rămâne în discuție la fiecare tur de-atunci încolo. Cu `caut`,
 *  ce s-a potrivit — cu explicația lui. Aceeași socoteală ca la feliile
 *  planului (v. `unelte.ts`).
 *
 *  CĂUTAREA E PE CUVINTE, FĂRĂ DIACRITICE, ȘI ÎN COMENTARIUL ÎNTREG. Cele trei
 *  laolaltă sunt ce desparte o căutare care răspunde de una care trimite omul
 *  să ghicească nume: «lărgește holul» se scrie «largeste hol» și trebuie să
 *  cadă pe `mutaLaturaCamerei`, al cărei nume nu conține niciunul din cele două
 *  cuvinte. Întâi se cer TOATE cuvintele (răspunsul scurt și potrivit), apoi,
 *  dacă n-a rămas nimic, ORICARE (răspunsul larg, care măcar arată familia).
 *
 *  NU SE ÎNTOARCE NICIODATĂ GOL. O căutare fără rezultat e chiar clipa în care
 *  un model începe să încerce nume inventate, unul după altul — s-a și văzut,
 *  pe «lărgește holul»: douăzeci de încercări până la pârghia care exista de la
 *  început. Deci când nu s-a potrivit nimic, se întorc gesturile editorului,
 *  toate, cu o notă care spune limpede că e lista întreagă, nu un răspuns la
 *  ce s-a cerut.
 */
export async function suprafata(caut?: string): Promise<{
  fisiere: Record<string, string[]>;
  gasite: number;
  nota?: string;
}> {
  const toate = await parghii();
  const cuvinte = normalizeaza(caut ?? "")
    .split(/[^a-z0-9]+/).filter((c) => c.length >= 2);

  if (!cuvinte.length) {
    const fisiere: Record<string, string[]> = {};
    let gasite = 0;
    for (const [fisier, lista] of Object.entries(toate)) {
      fisiere[fisier] = lista.map((f) => f.semnatura);
      gasite += lista.length;
    }
    return { fisiere, gasite,
             nota: "Numai semnăturile. Cere `caut` (unul sau mai multe "
               + "cuvinte, diacriticele nu contează) ca să vezi și "
               + "explicațiile funcțiilor care te interesează." };
  }

  const strange = (potrivire: (f: Functie) => boolean) => {
    const fisiere: Record<string, string[]> = {};
    let gasite = 0;
    for (const [fisier, lista] of Object.entries(toate)) {
      const alese = lista.filter(potrivire);
      if (!alese.length) continue;
      gasite += alese.length;
      fisiere[fisier] = alese.map((f) =>
        f.doc ? `${f.semnatura} — ${f.doc}` : f.semnatura);
    }
    return { fisiere, gasite };
  };

  const toateCuvintele = strange((f) => cuvinte.every((c) => f.cauta.includes(c)));
  if (toateCuvintele.gasite) return toateCuvintele;

  if (cuvinte.length > 1) {
    const vreunul = strange((f) => cuvinte.some((c) => f.cauta.includes(c)));
    if (vreunul.gasite) {
      return { ...vreunul,
               nota: `Niciuna nu are toate cuvintele; astea au măcar unul din `
                 + `«${cuvinte.join(", ")}».` };
    }
  }

  // Mâna goală e ce pornește ghicitul: mai bine lista gesturilor, spusă ca
  // atare.
  const gesturi = toate["editor/model"] ?? [];
  return {
    fisiere: { "editor/model": gesturi.map((f) => f.semnatura) },
    gasite: gesturi.length,
    nota: `Nimic pentru «${cuvinte.join(" ")}». Mai jos sunt TOATE gesturile `
      + "editorului (`editor/model`), nu un răspuns la ce ai cerut — caută în "
      + "ele numele lucrului pe care vrei să-l atingi (zid, cameră, gol, "
      + "piesă, aparat) și cere-l din nou cu un cuvânt din listă.",
  };
}
